import React, { useEffect, useState, useRef } from "react";
import { useGSAPReveal, useParallax } from "../util/useGSAPReveal";
import { Link } from "react-router-dom";

export const Intro: React.FC = () => {
  const [isMobileView, setIsMobileView] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // GSAP scroll-triggered animations
  const leftContentRef = useGSAPReveal({ variant: 'stagger-children', start: 'top 95%', staggerAmount: 0.1, distance: 40 });

  // Stagger the right side cards coming up
  const rightContentRef = useGSAPReveal({ variant: 'stagger-children', start: 'top 85%', staggerAmount: 0.15, delay: 0.3, distance: 60 });

  // Subtle parallax on the whole section background
  const parallaxRef = useParallax(0.05);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth <= 900);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => {
      window.removeEventListener("resize", checkScreenSize);
    };
  }, []);

  return (
    <section
      ref={containerRef}
      aria-label="Introduction"
      style={{
        position: "relative",
        width: "100%",
        minHeight: "100vh",
        background: "#0A0A0A",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* Aurora Background Effects */}
      <div ref={parallaxRef as any} style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        {/* Deep background grid for texture */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0.15,
            backgroundSize: "60px 60px",
            backgroundImage: `
              linear-gradient(to right, rgba(245, 245, 245, 0.05) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(245, 245, 245, 0.05) 1px, transparent 1px)
            `,
            maskImage: "radial-gradient(ellipse at center, rgba(0,0,0,1) 0%, transparent 80%)",
            WebkitMaskImage: "radial-gradient(ellipse at center, rgba(0,0,0,1) 0%, transparent 80%)",
            zIndex: 0,
          }}
        />
      </div>

      {/* Main Layout Container */}
      <div
        style={{
          position: "relative",
          zIndex: 2,
          display: "flex",
          flexDirection: isMobileView ? "column" : "row",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          maxWidth: "1400px",
          margin: "0 auto",
          padding: isMobileView ? "100px 20px 40px" : "80px 80px 0 80px",
          gap: isMobileView ? "60px" : "40px",
        }}
      >
        {/* Left Column: Typography & CTAs */}
        <div
          ref={leftContentRef}
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            maxWidth: isMobileView ? "100%" : "650px",
            zIndex: 5
          }}
        >
          {/* Top Tech Badge */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "6px 14px",
              background: "rgba(245, 245, 245, 0.03)",
              border: "1px solid rgba(245, 245, 245, 0.1)",
              borderRadius: "100px",
              marginBottom: "32px",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 17L10 11 4 5" /><line x1="12" y1="19" x2="20" y2="19" />
            </svg>
            <span style={{
              fontSize: "12px",
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              color: "#A3A3A3",
              fontFamily: "'JetBrains Mono', monospace"
            }}>
              System Architect & AI Engineer
            </span>
          </div>

          {/* Title */}
          <h1
            style={{
              fontSize: isMobileView ? "48px" : "clamp(64px, 6vw, 90px)",
              fontWeight: 600,
              fontFamily: "'Space Grotesk', sans-serif",
              letterSpacing: "-0.04em",
              lineHeight: "1.05",
              margin: "0 0 20px 0",
              color: "#F5F5F5",
            }}
          >
            Asim Hafeez<span style={{ color: "#C4956A" }}>.</span>
          </h1>

          {/* Staggered Taglines - Left Aligned */}
          <div
            style={{
              fontSize: isMobileView ? "18px" : "22px",
              color: "#F5F5F5",
              fontWeight: 400,
              fontFamily: "'Space Grotesk', sans-serif",
              marginBottom: "28px",
              letterSpacing: "0.01em",
              display: "flex",
              flexWrap: "wrap",
              gap: isMobileView ? "8px" : "16px",
              alignItems: "center"
            }}
          >
            <span style={{ opacity: 0.9 }}>Engineering Lead</span>
            <span style={{ color: "#C4956A", opacity: 0.5, fontStyle: "italic" }}>/</span>
            <span style={{ opacity: 0.9 }}>Lead Author</span>
            <span style={{ color: "#C4956A", opacity: 0.5, fontStyle: "italic" }}>/</span>
            <span style={{ opacity: 0.9 }}>Architect</span>
          </div>

          {/* Description */}
          <p
            style={{
              fontSize: isMobileView ? "16px" : "18px",
              color: "#A3A3A3",
              lineHeight: "1.7",
              fontWeight: 300,
              marginBottom: "48px",
              maxWidth: "580px",
            }}
          >
            Building high-performance <span style={{ color: "#F5F5F5", fontWeight: 400 }}>scalable systems</span> and leading engineering teams at the intersection of architecture, AI, and cloud infrastructure. Based in Norway.
          </p>

          {/* Enhanced CTAs */}
          <div
            style={{
              display: "flex",
              gap: isMobileView ? "16px" : "20px",
              flexDirection: isMobileView ? "column" : "row",
              width: isMobileView ? "100%" : "auto",
            }}
          >
            {/* Primary CTA — Copper */}
            <a
              href="mailto:asimhafeezz@icloud.com"
              className="hero-btn-primary"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                padding: isMobileView ? "16px 32px" : "16px 36px",
                background: "#C4956A",
                color: "#0A0A0A",
                textDecoration: "none",
                borderRadius: "100px",
                fontSize: "15px",
                fontWeight: 600,
                transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                cursor: "pointer",
                letterSpacing: "0.02em",
                width: isMobileView ? "100%" : "auto",
                border: "1px solid transparent",
              }}
            >
              <span>Get In Touch</span>
              <svg
                className="hero-btn-icon"
                width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                style={{ transition: "transform 0.3s ease" }}
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>

            {/* Secondary CTA — Glassmorphism Ghost */}
            <Link
              to="/publications"
              className="hero-btn-secondary"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                padding: isMobileView ? "16px 32px" : "16px 36px",
                background: "rgba(245, 245, 245, 0.03)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                color: "#F5F5F5",
                textDecoration: "none",
                borderRadius: "100px",
                fontSize: "15px",
                fontWeight: 500,
                border: "1px solid rgba(245, 245, 245, 0.1)",
                transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                cursor: "pointer",
                letterSpacing: "0.02em",
                width: isMobileView ? "100%" : "auto",
              }}
            >
              <span>Explore My Book</span>
            </Link>
          </div>
        </div>

        {/* Right Column: Floating Glass Cards */}
        {!isMobileView && (
          <div
            ref={rightContentRef}
            style={{
              flex: 1,
              position: 'relative',
              height: '500px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              perspective: '1000px'
            }}
          >
            {/* Base Glowing Element for cards */}
            <div style={{
              position: 'absolute',
              width: '300px',
              height: '300px',
              background: '#C4956A',
              filter: 'blur(100px)',
              opacity: 0.15,
              borderRadius: '50%',
              zIndex: 0
            }} />

            {/* Floating Card 1: Main Book Highlight */}
            <Link
              to="/publications"
              className="glass-card article-hover"
              style={{
                position: 'absolute',
                top: '5%',
                right: '10%',
                width: '320px',
                padding: '24px',
                borderRadius: '20px',
                zIndex: 3,
                transform: 'rotateZ(2deg)',
                textDecoration: 'none',
                display: 'block',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(196, 149, 106, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C4956A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" /></svg>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#C4956A', fontFamily: "'JetBrains Mono', monospace", fontWeight: 500 }}>AUTHOR</div>
                  <div style={{ fontSize: '15px', color: '#F5F5F5', fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif" }}>Designing Scalable Systems</div>
                </div>
              </div>
              <p style={{ fontSize: '13px', color: '#A3A3A3', lineHeight: '1.6', marginBottom: '12px' }}>
                A comprehensive guide covering API protocols, microservices, cloud solutions, and system security.
              </p>
              <div className="article-read-text" style={{ fontSize: '12px', color: '#C4956A', display: 'flex', alignItems: 'center', gap: '4px', transition: 'gap 0.2s ease' }}>
                Explore book <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
              </div>
            </Link>

            {/* Floating Card 2: Core Stack Badge */}
            <a
              href="#about"
              className="glass-card article-hover"
              style={{
                position: 'absolute',
                bottom: '25%',
                left: '0%',
                width: '280px',
                padding: '20px',
                borderRadius: '16px',
                zIndex: 2,
                transform: 'rotateZ(-3deg)',
                textDecoration: 'none',
                display: 'block',
              }}
            >
              <div style={{ fontSize: '11px', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '12px', fontWeight: 600 }}>Core Expertise</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                {['System Design', 'AI Engineering', 'Blockchain', 'AWS'].map((tag, i) => (
                  <span key={i} style={{
                    padding: '4px 10px',
                    background: 'rgba(245, 245, 245, 0.05)',
                    border: '1px solid rgba(245, 245, 245, 0.1)',
                    borderRadius: '100px',
                    fontSize: '11px',
                    color: '#e5e5e5',
                    fontFamily: "'JetBrains Mono', monospace"
                  }}>
                    {tag}
                  </span>
                ))}
              </div>
              <div className="article-read-text" style={{ fontSize: '12px', color: '#C4956A', display: 'flex', alignItems: 'center', gap: '4px', transition: 'gap 0.2s ease' }}>
                About me <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
              </div>
            </a>

            {/* Floating Card 3: Latest Article Highlight */}
            <Link
              to="/articles"
              className="glass-card article-hover"
              style={{
                position: 'absolute',
                bottom: '-2%',
                right: '5%',
                width: '320px',
                padding: '20px',
                borderRadius: '16px',
                zIndex: 4,
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                transform: 'rotateZ(1deg)',
                textDecoration: 'none',
              }}
            >
              <div style={{
                minWidth: '56px',
                height: '56px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, rgba(245, 245, 245, 0.1) 0%, rgba(245, 245, 245, 0.02) 100%)',
                border: '1px solid rgba(245, 245, 245, 0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#F5F5F5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" /><polyline points="10 9 9 9 8 9" /><polyline points="10 13 9 13 8 13" /><polyline points="10 17 9 17 8 17" /></svg>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', color: '#A3A3A3', fontFamily: "'JetBrains Mono', monospace", marginBottom: '6px' }}>WRITINGS & PUBLICATIONS</div>
                <div style={{ fontSize: '14px', color: '#F5F5F5', fontWeight: 500, lineHeight: '1.4', marginBottom: '4px' }}>Technical Architecture & System Design</div>
                <div className="article-read-text" style={{ fontSize: '12px', color: '#C4956A', display: 'flex', alignItems: 'center', gap: '4px', transition: 'gap 0.2s ease' }}>
                  Read articles <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                </div>
              </div>
            </Link>

          </div>
        )}
      </div>

      <style>{`
        /* Button Hover States */
        .hero-btn-primary:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(196, 149, 106, 0.3) !important;
          background: #D4A87A !important;
        }
        .hero-btn-primary:hover .hero-btn-icon {
          transform: translateX(4px);
        }

        .hero-btn-secondary:hover {
          background: rgba(245, 245, 245, 0.08) !important;
          transform: translateY(-4px);
          border-color: rgba(196, 149, 106, 0.4) !important;
          color: #C4956A !important;
        }

        /* Glassmorphism Styles for Cards */
        .glass-card {
          background: rgba(20, 20, 20, 0.6);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
          transition: border-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        
        .glass-card:hover {
          border-color: rgba(196, 149, 106, 0.3);
          box-shadow: 0 20px 40px rgba(196, 149, 106, 0.15), 0 0 0 1px rgba(196, 149, 106, 0.1) inset;
        }

        /* Specific Hover states for Articles card */
        .article-hover:hover {
           transform: translateY(-8px) rotateZ(1deg); /* subtle lift up, maintaining tilt */
        }
        .article-hover:hover .article-read-text {
            color: #D4A87A !important;
            gap: 8px !important;
        }
      `}</style>
    </section>
  );
};

export default Intro;
